/* eslint-disable consistent-return */
import { getApiPath, apiPaths, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- CREATE
export const CREATE_FLOW_GROUP_SUCCESS = 'CREATE_FLOW_GROUP_SUCCESS'
export const CREATE_FLOW_GROUP_FAILURE = 'CREATE_FLOW_GROUP_FAILURE'

// -- UPDATE
export const UPDATE_FLOW_GROUP_SUCCESS = 'UPDATE_FLOW_GROUP_SUCCESS'
export const UPDATE_FLOW_GROUP_FAILURE = 'UPDATE_FLOW_GROUP_FAILURE'

// -- GET (array of objects)
export const GET_FLOW_GROUPS_SUCCESS = 'GET_FLOW_GROUPS_SUCCESS'
export const GET_FLOW_GROUPS_FAILURE = 'GET_FLOW_GROUPS_FAILURE'

// -- GET (one object)
// export const GET_FLOW_GROUP_SUCCESS = 'GET_FLOW_GROUP_SUCCESS'
// export const GET_FLOW_GROUP_FAILURE = 'GET_FLOW_GROUP_FAILURE'

// -- DELETE
export const DELETE_FLOW_GROUP_SUCCESS = 'DELETE_FLOW_GROUP_SUCCESS'
export const DELETE_FLOW_GROUP_FAILURE = 'DELETE_FLOW_GROUP_FAILURE'

// -- SET CURRENT FLOW GROUP
export const SET_CURRENT_FLOW_GROUP = 'SET_CURRENT_FLOW_GROUP'

// -- CACHE FLOW GROUP ACTIONS
export const CACHE_UPDATE_FLOW_GROUP_START_ACTION = 'CACHE_UPDATE_FLOW_GROUP_START_ACTION'
export const CACHE_UPDATE_FLOW_GROUP_CONDITION = 'CACHE_UPDATE_FLOW_GROUP_CONDITION'
export const CACHE_UPDATE_FLOW_GROUP_TASK = 'CACHE_UPDATE_FLOW_GROUP_TASK'
export const CACHE_UPDATE_FLOW_GROUP_TASK_ASSIGN = 'CACHE_UPDATE_FLOW_GROUP_TASK_ASSIGN'

// ____________________________________________________________

/**
 * Set a flow group as current.
 *
 * @param {object} flowGroup to be defined as current
 * @returns {object}
 */
export const setCurrentFlowGroup = (flowGroup) => ({
  type: SET_CURRENT_FLOW_GROUP,
  data: flowGroup
})

/**
 * Set the definition of a flow group start action in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the taskInstance
 * @param {object} startAction the start action to be updated
 * @returns {object}
 */
export const cacheUpdateFlowGroupStartAction = (flowGroup, startAction) => ({
  type: CACHE_UPDATE_FLOW_GROUP_START_ACTION,
  data: {
    flowGroup,
    startAction
  }
})

/**
 * Set the definition of a flow group condition in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the taskInstance
 * @param {object} condition the condition to be updated
 * @returns {object}
 */
export const cacheUpdateFlowGroupCondition = (flowGroup, condition) => ({
  type: CACHE_UPDATE_FLOW_GROUP_CONDITION,
  data: {
    flowGroup,
    condition
  }
})

/**
 * Set the definition of a flow group taskInstance in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the taskInstance
 * @param {object} taskInstance the task instance to be updated
 * @returns {object}
 */
export const cacheUpdateFlowGroupTask = (flowGroup, taskInstance) => ({
  type: CACHE_UPDATE_FLOW_GROUP_TASK,
  data: {
    flowGroup,
    taskInstance
  }
})

/**
 * Set the definition of a flow group taskInstance in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} taskInstance the task instance with the newly assigned task to be updated
 * @returns {object}
 */
export const cacheUpdateFlowGroupTaskAssign = (flowGroup, taskInstance) => ({
  type: CACHE_UPDATE_FLOW_GROUP_TASK_ASSIGN,
  data: {
    flowGroup,
    taskInstance
  }
})

/**
 * Create a new flowGroup broadcast two types of events:
 * - CREATE_FLOW_GROUP_SUCCESS: in case of success
 * - CREATE_FLOW_GROUP_FAILURE: in case of failure.
 *
 * @param {number} pid Project Id number
 * @param {number} sid Scenario Id number
 * @param {object} flowGroup Flow to create
 * @returns {Promise<object>}
 */
export const createFlowGroup = (pid, sid, flowGroup) => async (dispatch) => {
  if (!flowGroup) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_GROUP_CREATE, { pid, sid })
  const api = new Api(path)
  const result = await api.create(flowGroup)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowGroupsActions.createFlowGroup()', { path, flowGroup, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_FLOW_GROUP_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_FLOW_GROUP_FAILURE,
    data: result.data
  })
}

/**
 * Update the values of a flowGroup broadcast two types of events:
 * - UPDATE_FLOW_GROUP_SUCCESS: in case of success
 * - UPDATE_FLOW_GROUP_FAILURE: in case of failure.
 *
 * @param {number} pid Project Id number
 * @param {number} sid Scenario Id number
 * @param {object} flowGroup Flow to update
 * @returns {Promise<object>}
 */
export const updateFlowGroup = (pid, sid, flowGroup) => async (dispatch) => {
  if (!flowGroup) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_GROUP_UPDATE, { pid, sid, fid: flowGroup.id })
  const api = new Api(path)
  const result = await api.update(flowGroup)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowGroupsActions.updateFlowGroup()', { path, result, flowGroup })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_FLOW_GROUP_SUCCESS,
      data: result.data || null
      // errors: result.data?.errors || null
    })
  }
  return dispatch({
    type: UPDATE_FLOW_GROUP_FAILURE,
    data: result.data || null
    // errors: result.data?.errors || null
  })
}

/**
 * Get the flow lists form the API and broadcast two types of events:
 * - GET_FLOW_GROUPS_SUCCESS: in case of success
 * - GET_FLOW_GROUPS_FAILURE: in case of failure.
 *
 * @param {number} pid Project Id number
 * @param {number} sid Scenario Id number
 * @returns {Promise<object>} The data returns contain the
 * Ex: data: [
 *   {
 *     errors: []
 *     flow_group: {creation_date: '2022-02-22', description: '...', flows: [{...}, ...], id: '8beeeeda-a641-4eac-99fa-0439c31844e5', last_update: '2022-02-22', …}
 *   },
 *   {
 *     errors: [{...}, {...}, ...]
 *     flow_group: {...}
 *   },
 *   ...
 * ]
 */
export const getFlowGroups = (pid, sid) => async (dispatch) => {
  const path = getApiPath(apiPaths.FLOW_GROUP_LIST, { pid, sid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowGroupsActions.getFlowGroups()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_FLOW_GROUPS_SUCCESS,
      data: result.data.items || []
    })
  }
  return dispatch({
    type: GET_FLOW_GROUPS_FAILURE,
    data: result.data
  })
}

/**
 * Get a specific flow object form an fid, from the API and broadcast two types of events:
 * - GET_FLOW_GROUP_SUCCESS: in case of success
 * - GET_FLOW_GROUP_FAILURE: in case of failure.
 *
 * @param {number} pid Project Id number
 * @param {number} sid Scenario Id number
 * @param {number} fid FlowId to get
 * @returns {Promise<object>}
 */
// export const getFlowGroup = (pid, sid, fid) => async (dispatch) => {
//   const path = getApiPath(apiPaths.FLOW_GROUP_GET, { pid, sid, fid })
//   const api = new Api(path)
//   const result = await api.get()

//   Logger.trace(LoggerTypes.ACTION, '[ACTION] flowGroupsActions.getFlowGroup()', { path, result })

//   if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
//     return dispatch({
//       type: GET_FLOW_GROUP_SUCCESS,
//       data: result.data
//     })
//   }
//   return dispatch({
//     type: GET_FLOW_GROUP_FAILURE,
//     data: result.data
//   })
// }

/**
 * Delete a flowGroup and broadcast two types of events:
 * - DELETE_FLOW_GROUP_SUCCESS: in case of success
 * - DELETE_FLOW_GROUP_FAILURE: in case of failure.
 *
 * @param {number} pid Project Id UUID
 * @param {number} sid Scenario Id UUID.
 * @param {number} fid FlowGroup Id to delete.
 * @returns {Promise<object>}
 */
export const deleteFlowGroup = (pid, sid, fid) => async (dispatch) => {
  const path = getApiPath(apiPaths.FLOW_GROUP_DELETE, { pid, sid, fid })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowGroupsActions.deleteFlowGroup()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_FLOW_GROUP_SUCCESS,
      data: fid
    })
  }
  return dispatch({
    type: DELETE_FLOW_GROUP_FAILURE,
    data: result.data
  })
}
